import React, { useEffect, useRef, useState, useCallback } from "react";
import "./ActionBar.scss";

import likeImg from "./img/like.png";
import likedImg from "./img/liked.png";
import commentImg from "./img/comment.png";
import shareImg from "./img/share.png";
import saveImg from "./img/save.png";
import savedImg from "./img/saved.png";

export default function ActionBar({
  liked,
  saved,
  likeCount,
  commentCount,
  shareCount,
  savedCount,
  onLike,
  onComment,
  onShare,
  onSave,
  variant = "floating",
  context = "",
  className = "",
  showComments = true,
  showCounts = true,
  clean = false,
}) {
  const [likeBump, setLikeBump] = useState(false);
  const [saveBump, setSaveBump] = useState(false);

  const likedPrev = useRef(!!liked);
  const savedPrev = useRef(!!saved);

  const triggerBump = useCallback((type) => {
    // restart animation reliably
    if (type === "like") {
      setLikeBump(false);
      requestAnimationFrame(() => setLikeBump(true));
      setTimeout(() => setLikeBump(false), 420);
    } else {
      setSaveBump(false);
      requestAnimationFrame(() => setSaveBump(true));
      setTimeout(() => setSaveBump(false), 420);
    }
  }, []);

  // bump when state becomes true (also works for double-tap external updates)
  useEffect(() => {
    const now = !!liked;
    if (now && !likedPrev.current) triggerBump("like");
    likedPrev.current = now;
  }, [liked, triggerBump]);

  useEffect(() => {
    const now = !!saved;
    if (now && !savedPrev.current) triggerBump("save");
    savedPrev.current = now;
  }, [saved, triggerBump]);

  const handleLike = (e) => {
    e?.stopPropagation?.();
    onLike?.();
    triggerBump("like");
  };

  const handleSave = (e) => {
    e?.stopPropagation?.();
    onSave?.();
    triggerBump("save");
  };

  const handleComment = (e) => {
    e?.stopPropagation?.();
    onComment?.();
  };

  const handleShare = (e) => {
    e?.stopPropagation?.();
    onShare?.();
  };

  const ctxClass = context ? `actionbar--ctx-${String(context).toLowerCase()}` : "";

        return (
          <div
        className={[
          "actionbar",
          `actionbar--${variant}`,
          ctxClass,
          clean ? "actionbar--clean" : "",
          !showCounts ? "actionbar--no-counts" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
      {!clean && (
  <>
    <span className="actionbar__glass" aria-hidden="true" />

    <svg
      className="actionbar__filters"
      width="0"
      height="0"
      aria-hidden="true"
      focusable="false"
    >
      <filter
        id="cvanto-liquid-glass"
        x="-30%"
        y="-60%"
        width="160%"
        height="220%"
        colorInterpolationFilters="sRGB"
      >
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.012 0.06"
          numOctaves="2"
          seed="8"
          result="noise"
        />

        <feDisplacementMap
          in="SourceGraphic"
          in2="noise"
          scale="26"
          xChannelSelector="R"
          yChannelSelector="G"
          result="displaced"
        />

        <feGaussianBlur
          in="displaced"
          stdDeviation="0.35"
          result="blurred"
        />

        <feColorMatrix
          in="blurred"
          type="matrix"
          values="
            1 0 0 0 0
            0 1 0 0 0
            0 0 1 0 0
            0 0 0 0.92 0
          "
        />
      </filter>
    </svg>
  </>
)}

      <button
        className={`actionbar__btn ${liked ? "is-active" : ""} ${
          likeBump ? "is-bumped" : ""
        }`}
        onClick={handleLike}
        aria-label="Like"
        type="button"
      >
        <span className="actionbar__iconWrap">
          <img
            className={`actionbar__icon ${
              liked ? "actionbar__icon--noinvert" : ""
            }`}
            src={liked ? likedImg : likeImg}
            alt=""
            draggable="false"
          />
        </span>
        {showCounts && (
          <span className="actionbar__count">
            {likeCount || 0}
          </span>
        )}
        <span className="actionbar__spark" aria-hidden="true" />
      </button>

      {showComments && (
        <button
          className="actionbar__btn"
          onClick={handleComment}
          aria-label="Comment"
          type="button"
        >
          <span className="actionbar__iconWrap">
            <img className="actionbar__icon" src={commentImg} alt="" draggable="false" />
          </span>
          {showCounts && (
          <span className="actionbar__count">
            {commentCount || 0}
          </span>
        )}
        </button>
      )}

      <button
        className="actionbar__btn"
        onClick={handleShare}
        aria-label="Share"
        type="button"
      >
        <span className="actionbar__iconWrap">
          <img className="actionbar__icon" src={shareImg} alt="" draggable="false" />
        </span>
        {showCounts && (
        <span className="actionbar__count">
          {shareCount || 0}
        </span>
      )}
      </button>

      <button
        className={`actionbar__btn ${saved ? "is-active" : ""} ${
          saveBump ? "is-bumped" : ""
        }`}
        onClick={handleSave}
        aria-label="Save"
        type="button"
      >
        <span className="actionbar__iconWrap">
          <img
            className="actionbar__icon"
            src={saved ? savedImg : saveImg}
            alt=""
            draggable="false"
          />
        </span>
        {showCounts && (
          <span className="actionbar__count">
            {savedCount || 0}
          </span>
        )}
        <span className="actionbar__spark" aria-hidden="true" />
      </button>
    </div>
  );
}